import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ColumnDef } from '@tanstack/react-table';
import { Box, Chip, Divider, Drawer, IconButton, Stack, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { MdCallMade, MdCallReceived, MdClose, MdOutlineRemoveRedEye } from 'react-icons/md';

import Search from 'components/Common/Search';
import Select from 'components/Common/Select';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import DateRangePicker from 'components/Common/DateRangePicker';
import { formatDate, titleCase } from 'utils/helperFunctions';

import { useGetBusinesses } from '@/apis/agency/hook';
import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';
import { useGetAgencySmsLog, useGetAgencySmsLogs } from '@/apis/agency/smsLogs/hook';
import { AgencySmsLogResponse, SmsLogDirection, SmsLogOrigin, SmsLogStatus } from '@/apis/agency/smsLogs/types';
import { BusinessProfileResponse } from '@/apis/business/types';

type SearchField = 'from_number_cont' | 'to_number_cont' | 'tag_cont';

interface FilterFormData {
  filter: {
    business_id_eq: string;
    provider_eq: string;
    status_eq: string;
    direction_eq: string;
    origin_eq: string;
    created_at_in: string;
  };
  search_field: SearchField;
}

const defaultFormValues: FilterFormData = {
  filter: {
    business_id_eq: ' ',
    provider_eq: ' ',
    status_eq: ' ',
    direction_eq: ' ',
    origin_eq: ' ',
    created_at_in: ''
  },
  search_field: 'from_number_cont'
};

const PROVIDER_OPTIONS = [
  { value: ' ', label: 'All' },
  { value: '0', label: 'Twilio' },
  { value: '1', label: 'Bandwidth' },
  { value: '2', label: 'NetSapiens' }
];

const STATUS_OPTIONS = [
  { value: ' ', label: 'All' },
  { value: '0', label: 'Sent' },
  { value: '1', label: 'Failed' },
  { value: '2', label: 'Delivered' },
  { value: '3', label: 'Received' }
];

const DIRECTION_OPTIONS = [
  { value: ' ', label: 'All' },
  { value: '0', label: 'Outgoing' },
  { value: '1', label: 'Incoming' }
];

const ORIGIN_OPTIONS = [
  { value: ' ', label: 'All' },
  { value: 'agency', label: 'Agency' },
  { value: 'business', label: 'Business' }
];

const SEARCH_FIELD_OPTIONS = [
  { value: 'from_number_cont', label: 'From' },
  { value: 'to_number_cont', label: 'To' },
  { value: 'tag_cont', label: 'Tag' }
];

const SEARCH_FIELD_PLACEHOLDER: Record<SearchField, string> = {
  from_number_cont: 'Search by From number',
  to_number_cont: 'Search by To number',
  tag_cont: 'Search by tag'
};

const BODY_PREVIEW_LIMIT = 40;

function truncate(text: string | null | undefined, limit: number) {
  if (!text) return '—';
  const trimmed = text.trim();
  if (trimmed.length <= limit) return trimmed;
  return `${trimmed.slice(0, limit)}…`;
}

function getStatusPalette(theme: Theme, status: SmsLogStatus | undefined) {
  const map: Record<SmsLogStatus, { color: string; bg: string }> = {
    sent: { color: theme.palette.info.dark ?? '#1E40AF', bg: theme.palette.info.light ?? '#EEF5FF' },
    failed: { color: theme.palette.error.main, bg: theme.palette.error.light },
    delivered: { color: theme.palette.success.main, bg: theme.palette.success.light },
    received: { color: '#7C00B1', bg: '#F5DFFF' }
  };
  return status && map[status] ? map[status] : { color: '#374151', bg: '#F3F4F6' };
}

function StatusBadge({ status }: { status: SmsLogStatus | undefined }) {
  const theme = useTheme();
  const { color, bg } = getStatusPalette(theme, status);
  return (
    <Chip
      label={status ? titleCase(status) : '—'}
      size='small'
      sx={{
        fontWeight: 550,
        fontSize: { xs: 11, lg: 12 },
        borderRadius: '10px',
        color,
        bgcolor: bg,
        height: 24
      }}
    />
  );
}

function DirectionBadge({ direction }: { direction: SmsLogDirection | undefined }) {
  const isOutgoing = direction === 'outgoing';
  const label = direction ? titleCase(direction) : '—';
  const color = isOutgoing ? '#1E40AF' : '#7C00B1';
  const bg = isOutgoing ? '#EEF5FF' : '#F5DFFF';
  return (
    <Box display='inline-flex' alignItems='center' gap={0.75}>
      {isOutgoing ? <MdCallMade color={color} size={16} /> : <MdCallReceived color={color} size={16} />}
      <Typography
        component='span'
        sx={{
          fontSize: { xs: 11, lg: 12 },
          fontWeight: 550,
          borderRadius: '10px',
          px: 1,
          py: 0.25,
          color,
          bgcolor: bg
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function OriginBadge({ origin }: { origin: SmsLogOrigin | null | undefined }) {
  if (!origin) {
    return <Typography sx={{ fontSize: { xs: 13, lg: 14 }, color: 'text.secondary' }}>—</Typography>;
  }
  const isAgency = origin === 'agency';
  const color = isAgency ? '#1E40AF' : '#077F13';
  const bg = isAgency ? '#EEF5FF' : '#DBFCDE';
  return (
    <Chip
      label={titleCase(origin)}
      size='small'
      sx={{
        fontWeight: 550,
        fontSize: { xs: 11, lg: 12 },
        borderRadius: '10px',
        color,
        bgcolor: bg,
        height: 24
      }}
    />
  );
}

function OriginWithDirectionCell({
  origin,
  direction
}: {
  origin: SmsLogOrigin | null | undefined;
  direction: SmsLogDirection | undefined;
}) {
  const isOutgoing = direction === 'outgoing';
  const iconColor = isOutgoing ? '#1E40AF' : '#7C00B1';
  const tooltip = direction ? titleCase(direction) : 'Unknown direction';
  return (
    <Box display='inline-flex' alignItems='center' gap={0.75}>
      {direction && (
        <Tooltip title={tooltip} placement='top'>
          <Box component='span' display='inline-flex'>
            {isOutgoing ? <MdCallMade color={iconColor} size={16} /> : <MdCallReceived color={iconColor} size={16} />}
          </Box>
        </Tooltip>
      )}
      <OriginBadge origin={origin} />
    </Box>
  );
}

export default function AgencySmsLogs() {
  const theme = useTheme();
  const { control, handleSubmit, watch } = useForm<FilterFormData>({
    defaultValues: defaultFormValues
  });

  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();
  const [searchText, setSearchText] = useState<null | string>(null);
  const [filterSearchText, setFilterSearchText] = useState<null | string>(null);
  const [selectedSmsLogId, setSelectedSmsLogId] = useState<number | null>(null);

  const [appliedFilters, setAppliedFilters] = useState<FilterFormData>(defaultFormValues);

  const { data: businesses, isLoading: isBusinessesLoading } = useGetBusinesses({
    filter: { account_status_eq: 0 },
    per_page: 9999
  });

  const businessCreatedAtById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of (businesses?.data ?? []) as BusinessProfileResponse[]) {
      if (item.created_at) map[String(item.id)] = item.created_at;
    }
    return map;
  }, [businesses?.data]);

  const businessesOptions = useMemo(
    () =>
      ((businesses?.data ?? []) as BusinessProfileResponse[]).map((business) => ({
        value: business.id,
        label: business.name
      })),
    [businesses?.data]
  );

  const { data: agencyOrganization } = useGetAgencyOrganizations();

  const watchedValues = watch();
  const watchedFiltersStr = JSON.stringify(watchedValues);

  useEffect(() => {
    setAppliedFilters((prev) => {
      const changed = JSON.stringify(prev) !== watchedFiltersStr;
      if (changed) resetPage();
      return JSON.parse(watchedFiltersStr) as FilterFormData;
    });
  }, [watchedFiltersStr, resetPage]);

  useEffect(() => {
    setFilterSearchText(searchText);
    resetPage();
  }, [searchText, resetPage]);

  const tillDateCreatedAt = useMemo(() => {
    const bid = watchedValues?.filter?.business_id_eq;
    if (bid && bid !== ' ' && businessCreatedAtById[String(bid)]) {
      return businessCreatedAtById[String(bid)];
    }
    return agencyOrganization?.created_at;
  }, [watchedValues?.filter?.business_id_eq, businessCreatedAtById, agencyOrganization?.created_at]);

  const buildParams = () => {
    const f = appliedFilters.filter;
    const params: Record<string, unknown> = { page: currentPage, per_page: perPage };
    const str = (v: unknown) => (v != null ? String(v).trim() : '');
    if (str(f?.business_id_eq)) params['filter[business_id_eq]'] = str(f.business_id_eq);
    if (str(f?.provider_eq)) params['filter[provider_eq]'] = str(f.provider_eq);
    if (str(f?.status_eq)) params['filter[status_eq]'] = str(f.status_eq);
    if (str(f?.direction_eq)) params['filter[direction_eq]'] = str(f.direction_eq);
    if (str(f?.origin_eq)) params['filter[origin_eq]'] = str(f.origin_eq);
    const createdIn = str(f?.created_at_in);
    if (createdIn) {
      const [start, end] = createdIn.split(',');
      if (start) params['filter[created_at_gteq]'] = start.trim();
      if (end) params['filter[created_at_lteq]'] = end.trim();
    }
    if (filterSearchText) {
      params[`filter[${appliedFilters.search_field}]`] = filterSearchText;
    }
    return params;
  };

  const { data: smsLogs, isLoading: isSmsLogsLoading } = useGetAgencySmsLogs(buildParams());

  const onSubmit = (data: FilterFormData) => {
    setAppliedFilters(data);
    resetPage();
  };

  return (
    <>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        marginBottom={3}
        flexDirection={{ xs: 'column', lg: 'row' }}
        gap={{ xs: 2, lg: 0 }}
        sx={{ width: '100%' }}
      >
        <Typography variant='h6' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
          SMS Logs
        </Typography>
        <Box
          component='form'
          onSubmit={handleSubmit(onSubmit)}
          display='flex'
          gap={1}
          flexWrap='wrap'
          flexDirection={{ xs: 'column', lg: 'row' }}
          sx={{ width: { xs: '100%', lg: 'auto' } }}
        >
          <Box sx={{ width: { xs: '100%', lg: '180px' } }}>
            <Select
              name='filter.business_id_eq'
              label='Business'
              control={control}
              sortOrder='none'
              searchable
              disabled={isBusinessesLoading}
              options={[{ value: ' ', label: 'All' }, ...businessesOptions]}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '180px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '150px' } }}>
            <Select
              name='filter.provider_eq'
              label='Provider'
              control={control}
              options={PROVIDER_OPTIONS}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '150px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '150px' } }}>
            <Select
              name='filter.status_eq'
              label='Status'
              control={control}
              options={STATUS_OPTIONS}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '150px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '150px' } }}>
            <Select
              name='filter.direction_eq'
              label='Direction'
              control={control}
              options={DIRECTION_OPTIONS}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '150px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '150px' } }}>
            <Select
              name='filter.origin_eq'
              label='Origin'
              control={control}
              options={ORIGIN_OPTIONS}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '150px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '270px' }, minWidth: { xs: '100%', lg: '270px' } }}>
            <DateRangePicker name='filter.created_at_in' control={control} createdAt={tillDateCreatedAt} />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '130px' } }}>
            <Select
              name='search_field'
              label='Search In'
              control={control}
              options={SEARCH_FIELD_OPTIONS}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '130px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '220px' } }}>
            <Search
              size='small'
              sx={{
                minWidth: { xs: '100%', lg: 220 },
                width: { xs: '100%', lg: 'auto' },
                '& .MuiInputBase-root': {
                  backgroundColor: theme.palette.background.paper
                }
              }}
              searchText={searchText}
              setSearchText={setSearchText}
              placeholder={SEARCH_FIELD_PLACEHOLDER[watchedValues.search_field]}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ width: '100%', overflowX: 'auto', mb: { xs: 2, lg: 0 } }}>
        <DataTable
          data={smsLogs?.data ?? []}
          columns={columns(setSelectedSmsLogId)}
          isHeaderSticky
          isLoading={isSmsLogsLoading}
        />
      </Box>
      <Box sx={{ pb: { xs: 2, lg: 0 } }}>
        <Pagination
          totalPages={smsLogs?.meta?.total_pages}
          totalCount={smsLogs?.meta?.total_count ?? 0}
          currentPage={currentPage}
          perPage={perPage}
          onChange={setPage}
          onPerPageChange={setPerPage}
        />
      </Box>

      <SmsLogDetailDrawer smsLogId={selectedSmsLogId} onClose={() => setSelectedSmsLogId(null)} />
    </>
  );
}

const columns = (onView: (id: number) => void): ColumnDef<AgencySmsLogResponse>[] => [
  {
    header: 'DATE & TIME',
    size: 180,
    cell: ({ row }) => (
      <Typography sx={{ fontSize: { xs: 13, lg: 14 } }}>
        {formatDate(row.original.sent_at ?? row.original.created_at)}
      </Typography>
    )
  },
  {
    header: 'ORIGIN',
    size: 140,
    cell: ({ row }) => <OriginWithDirectionCell origin={row.original.origin} direction={row.original.direction} />
  },
  {
    header: 'BUSINESS',
    size: 160,
    cell: ({ row }) => (
      <Typography sx={{ fontSize: { xs: 13, lg: 14 } }}>{row.original.business?.name ?? '—'}</Typography>
    )
  },
  {
    header: 'PROVIDER',
    size: 120,
    cell: ({ row }) => (
      <Typography sx={{ fontSize: { xs: 13, lg: 14 } }}>
        {row.original.provider ? titleCase(row.original.provider) : '—'}
      </Typography>
    )
  },
  {
    header: 'FROM',
    size: 150,
    cell: ({ row }) => (
      <Typography sx={{ fontSize: { xs: 13, lg: 14 }, wordBreak: 'break-word' }}>
        {row.original.from_number ?? '—'}
      </Typography>
    )
  },
  {
    header: 'TO',
    size: 150,
    cell: ({ row }) => (
      <Typography sx={{ fontSize: { xs: 13, lg: 14 }, wordBreak: 'break-word' }}>
        {row.original.to_number ?? '—'}
      </Typography>
    )
  },
  {
    header: 'BODY',
    size: 220,
    cell: ({ row }) => {
      const body = row.original.body;
      return (
        <Tooltip title={body ?? ''} placement='top-start'>
          <Typography sx={{ fontSize: { xs: 13, lg: 14 }, wordBreak: 'break-word' }}>
            {truncate(body, BODY_PREVIEW_LIMIT)}
          </Typography>
        </Tooltip>
      );
    }
  },
  {
    header: 'STATUS',
    size: 110,
    cell: ({ row }) => <StatusBadge status={row.original.status} />
  },
  {
    header: 'ACTION',
    size: 80,
    cell: ({ row }) => (
      <IconButton onClick={() => onView(row.original.id)} sx={{ padding: { xs: '4px', lg: '8px' } }}>
        <MdOutlineRemoveRedEye color='#777777' size={22} />
      </IconButton>
    )
  }
];

interface DrawerProps {
  smsLogId: number | null;
  onClose: () => void;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack direction='row' spacing={2} alignItems='flex-start'>
      <Typography sx={{ minWidth: 130, color: 'text.secondary', fontSize: 13, fontWeight: 500 }}>{label}</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Typography sx={{ fontSize: 14, wordBreak: 'break-word' }}>{value}</Typography>
        ) : (
          value
        )}
      </Box>
    </Stack>
  );
}

function SmsLogDetailDrawer({ smsLogId, onClose }: DrawerProps) {
  const theme = useTheme();
  const { data: smsLog, isLoading } = useGetAgencySmsLog(smsLogId);
  const isFailed = smsLog?.status === 'failed';

  return (
    <Drawer
      anchor='right'
      open={smsLogId !== null}
      onClose={onClose}
      slotProps={{
        backdrop: { invisible: false },
        paper: { sx: { width: { xs: '100%', sm: 520 }, maxWidth: '100%' } }
      }}
    >
      <Box height='100%' display='flex' flexDirection='column' bgcolor='background.paper'>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          px={3}
          py={2}
          borderBottom='1px solid'
          borderColor='divider'
        >
          <Box>
            <Typography fontSize={18} fontWeight={600}>
              SMS Log Details
            </Typography>
            {smsLog?.id && (
              <Typography fontSize={12} color='text.secondary'>
                ID #{smsLog.id}
              </Typography>
            )}
          </Box>
          <IconButton size='small' onClick={onClose} aria-label='Close'>
            <MdClose size={22} />
          </IconButton>
        </Box>

        <Box flex={1} overflow='auto' px={3} py={2}>
          {isLoading || !smsLog ? (
            <Typography color='text.secondary' fontSize={14}>
              Loading…
            </Typography>
          ) : (
            <Stack spacing={2}>
              <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                <StatusBadge status={smsLog.status} />
                <DirectionBadge direction={smsLog.direction} />
                <OriginBadge origin={smsLog.origin} />
                {smsLog.call_phase && (
                  <Chip
                    label={titleCase(smsLog.call_phase.replace('_', ' '))}
                    size='small'
                    sx={{ fontWeight: 500, borderRadius: '10px', height: 24, bgcolor: '#F3F4F6', color: '#374151' }}
                  />
                )}
              </Stack>

              <Divider />

              <DetailRow label='Business' value={smsLog.business?.name ?? '—'} />
              <DetailRow label='Provider' value={smsLog.provider ? titleCase(smsLog.provider) : '—'} />
              <DetailRow label='Origin' value={smsLog.origin ? titleCase(smsLog.origin) : '—'} />
              <DetailRow label='From' value={smsLog.from_number ?? '—'} />
              <DetailRow label='To' value={smsLog.to_number ?? '—'} />
              <DetailRow label='Tag' value={smsLog.tag ?? '—'} />
              <DetailRow label='External ID' value={smsLog.external_id ?? '—'} />
              <DetailRow label='Sent At' value={smsLog.sent_at ? formatDate(smsLog.sent_at) : '—'} />
              <DetailRow label='Created At' value={formatDate(smsLog.created_at)} />

              <Divider />

              <Box>
                <Typography sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 500, mb: 1 }}>
                  Message Body
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'grey.50',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: 14
                  }}
                >
                  {smsLog.body || '—'}
                </Box>
              </Box>

              {isFailed && smsLog.error_message && (
                <Box>
                  <Typography sx={{ color: theme.palette.error.main, fontSize: 13, fontWeight: 600, mb: 1 }}>
                    Error Message
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: theme.palette.error.main,
                      backgroundColor: theme.palette.error.light,
                      color: theme.palette.error.dark,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: 14
                    }}
                  >
                    {smsLog.error_message}
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
